const { VUE_APP_OSS, VUE_APP_OSS_PREVIEW_KEY } = process.env

import { post, get, createUUID, path_version } from '@/httpRequest/http.js'
import { OssEncrypt } from '@/httpRequest/encrypt'
import JSBridge from '@fs/jsbridge/dist/lib/jsBridge.js'
// import { Toast } from 'vant'
import store from '@/store/openAccount/index'
import { i18n } from '@/i18n/openAccount'

import { getBaseUrl } from '@/utils'
import SparkMD5 from 'spark-md5'
/**
 * 文件上传功能模块
 *
 * 新增断点续传功能使用示例：
 *
 * import { fileUploadWithResume } from '@/apis/openAccount/fileUpLoad.js'
 *
 * // 使用断点续传上传文件
 * const uploadResult = await fileUploadWithResume({
 *     file: videoFile, // File对象
 *     onProgress: (progressInfo) => {
 *         const { percent, uploadedBytes, totalBytes, status } = progressInfo
 *         console.log(`${status}: ${percent}% (${uploadedBytes}/${totalBytes})`)
 *     },
 *     onError: (error) => {
 *         console.error('上传失败:', error)
 *     },
 *     retryTimes: 3 // 重试次数
 * })
 *
 * console.log('上传完成:', uploadResult.ossId, uploadResult.ossUrl)
 *
 * 注意事项：
 * 1. 需要安装 spark-md5 依赖用于计算文件MD5
 * 2. 支持断点续传，网络中断后会自动重试
 * 3. 自动处理文件分片上传
 * 4. 提供详细的上传进度回调
 */

const baseUrl = getBaseUrl(VUE_APP_OSS)
const downloadBaseUrl = getBaseUrl(VUE_APP_OSS, true)

/** 上传文件到OSS */
export const fileUpOss = (data, category = 'UserInfo', timeout = 30) => {
    if (JSBridge) {
        return post(baseUrl + `/oss/v1/Upload/${category}`, {
            data: data,
            method: 'OSS_UPLOAD_FILE',
        })
    }
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('filename', data.filename || '')
    formData.append('subAccountId', data.subAccountId || '')

    return post(baseUrl + `/oss/${path_version}/Upload/${category}`, {
        jsBridge: false,
        encrypt: 0, // 不加密
        data: formData,
        body: data,
        method: 'POST',
        timeout: timeout * 1000,
        type: 'from', // 提交的是一个 from 表单
        transformRequest: data => {
            data.toString = () => '[object FormData]'
            return data
        },
    })
}

/** 下载OSS文件 */
export const fileDownOss = data => {
    const ossProxy = data.proxy ?? false

    return get(
        `${downloadBaseUrl}/oss/${path_version}/Download?ossid=${data.ossId}&subAccountId=${data.subAccountId}${data.preview ? '&preview=1' : ''}`,
        {
            responseType: 'blob',
            encrypt: 0, //不加密
            noEncTag: true, //不加密
            method: JSBridge && ossProxy ? 'OSS_GET_FILE' : 'GET',
            timeout: 2 * 60 * 1000,
            jsBridge: ossProxy,
        }
    )
}

// 预览内容(新OSSID)
export const filePreviewUrl = (ossId, filename) => {
    try {
        const uin = localStorage.getItem('uin') || ''
        const requestId = createUUID()
        const session = `session=${localStorage.getItem('session')}` || ''
        const authInfo = {
            'x-request-id': requestId,
            'x-uin': uin,
            'x-session': session,
            reqTime: Date.now(),
        }
        console.log('filePreviewUrl ~ key', VUE_APP_OSS_PREVIEW_KEY)

        const authorize = OssEncrypt(JSON.stringify(authInfo), VUE_APP_OSS_PREVIEW_KEY)
        // console.log('🚀 ~ filePreviewUrl ~ authorize', authorize)

        return `${baseUrl}/oss/v1/Preview/${filename ? filename : ''}?ossid=${ossId}&x-request-id=${requestId}&authorize=${authorize}&preview=1`
    } catch (err) {
        console.log('file preview error: ', err)
        return ''
    }
}

// oss转本地文件Url
export const convertOssToUrl = async (ossArr = [], { loading = true, message = i18n.t('CaApply.wjjzz') } = {}) => {
    let timer
    try {
        const pAll = ossArr.map(item =>
            item.ossId
                ? fileDownOss({
                      ossId: item.ossId,
                      preview: item.preview,
                  })
                : ''
        )

        // loading && Toast.loading({
        //     duration: 0,
        //     message,
        // })
        loading && store.commit('user/updateShowLoading', [true, message])
        timer = setTimeout(() => {
            store.commit('user/updateShowLoading', false)
        }, 30000)
        const resArr = await Promise.allSettled(pAll)

        loading && store.commit('user/updateShowLoading', false)

        const localUrlArr = resArr.map(({ value, status }, index) => {
            const ossOpt = ossArr[index] ?? {}
            const ossId = value?.config?.ossId ?? ossOpt.ossId ?? ''

            let url = ''
            let blobData = new Blob()
            let fileName = ''
            if (status === 'fulfilled') {
                blobData = value?.data

                // if (JSBridge) {
                //     blobData = dataURLtoBlob(`data:${ossOpt.type};base64,${value?.data}`, { type: ossOpt.type })
                // } else {
                blobData = new Blob([blobData], { type: ossOpt.type })
                // }

                url = URL.createObjectURL(blobData)

                // 获取文件名
                const headerFileName = value?.headers?.['content-disposition']
                if (headerFileName) {
                    fileName = decodeURIComponent(
                        headerFileName.split(headerFileName.includes(`filename*=UTF-8''`) ? `filename*=UTF-8''` : '"')[1] || ''
                    )
                } else {
                    fileName = ossOpt.type ? `${ossId}.${ossOpt.type.split('/')[1]}` : ossId
                }
            }

            return {
                url,
                ossId,
                blobData,
                fileName,
            }
        })

        return localUrlArr
    } catch (err) {
        console.log('convertOssToUrl error:', err)
        loading && store.commit('user/updateShowLoading', false)
        return []
    } finally {
        clearTimeout(timer)
    }
}

/**
 * 计算文件MD5值
 * @param {File} file 文件对象
 * @returns {Promise<string>} MD5值
 */
const calculateFileMD5 = async file => {
    return new Promise((resolve, reject) => {
        const spark = new SparkMD5.ArrayBuffer()
        const fileReader = new FileReader()
        const chunkSize = 2097152 // 2MB per chunk
        const chunks = Math.ceil(file.size / chunkSize)
        let currentChunk = 0

        fileReader.onload = function (e) {
            spark.append(e.target.result)
            currentChunk++

            if (currentChunk < chunks) {
                loadNext()
            } else {
                resolve(spark.end())
            }
        }

        fileReader.onerror = function () {
            reject(new Error('文件读取失败'))
        }

        function loadNext() {
            const start = currentChunk * chunkSize
            const end = Math.min(start + chunkSize, file.size)
            fileReader.readAsArrayBuffer(file.slice(start, end))
        }

        loadNext()
    })
}

/**
 * 文件上传初始化接口
 * @param {Object} params 参数对象
 * @param {string} params.fileMd5 文件MD5
 * @param {string} params.filename 文件名
 * @param {number} params.filesize 文件大小(字节)
 * @returns {Promise<Object>} 返回uploadId、offset、chunkSize
 */
export const fileUploadInit = params => {
    return post(baseUrl + `/service-oss-assist/${path_version}/upload/init`, {
        data: {
            fileMd5: params.fileMd5,
            filename: params.filename,
            filesize: params.filesize,
        },
        encrypt: 0,
        method: 'POST',
        timeout: 30 * 1000,
    })
}

/**
 * 上传分片接口
 * @param {Object} params 参数对象
 * @param {string} params.uploadId 上传任务ID
 * @param {Blob} params.chunk 分片数据
 * @param {number} params.offset 分片起始偏移量
 * @param {number} params.chunkSize 分片大小
 * @param {number} params.fileSize 文件总大小
 * @returns {Promise<Object>} 返回done、nextOffset
 */
export const fileUploadChunk = params => {
    const { uploadId, chunk, offset, chunkSize, fileSize } = params

    // 计算Content-Range头
    const start = offset
    const end = Math.min(offset + chunkSize - 1, fileSize - 1)
    const contentRange = `bytes ${start}-${end}/${fileSize}`

    return post(baseUrl + `/service-oss-assist/${path_version}/upload/${uploadId}/chunk`, {
        data: chunk,
        headers: {
            'Content-Range': contentRange,
        },
        encrypt: 0,
        method: 'PUT',
        timeout: 60 * 1000,
        transformRequest: data => data, // 直接传输二进制数据
    })
}

/**
 * 上传完成接口
 * @param {string} uploadId 上传任务ID
 * @param {string} OssCategory OSS分类，默认'UserInfo'
 * @returns {Promise<Object>} 返回ossId、ossUrl
 */
export const fileUploadComplete = (uploadId, ossCategory = 'UserInfo') => {
    return post(baseUrl + `/service-oss-assist/${path_version}/upload/${uploadId}/complete`, {
        data: {
            ossCategory,
        },
        encrypt: 0,
        method: 'POST',
        timeout: 30 * 1000,
    })
}

/**
 * 断点续传文件上传主函数
 * @param {Object} options 配置选项
 * @param {File} options.file 文件对象
 * @param {Function} options.onProgress 进度回调函数 (percent, uploadedBytes, totalBytes) => {}
 * @param {Function} options.onError 错误回调函数 (error) => {}
 * @param {number} options.retryTimes 重试次数，默认3次
 * @returns {Promise<Object>} 返回ossId、ossUrl
 */
export const fileUploadWithResume = async options => {
    const { file, onProgress = () => {}, onError = () => {}, retryTimes = 3 } = options

    let currentRetry = 0
    const totalSize = file.size
    let startTime = null
    let lastUploadedBytes = 0
    let lastTime = null

    while (currentRetry <= retryTimes) {
        try {
            // 1. 计算文件MD5
            onProgress({
                percent: 0,
                uploadedBytes: 0,
                totalSize: file.size,
                status: 'calculating',
                statusText: i18n.t('LivingWitness.status_calculating') || '正在计算文件校验码...',
            })
            const fileMd5 = await calculateFileMD5(file)

            // 2. 初始化上传
            onProgress({
                percent: 0,
                uploadedBytes: 0,
                totalSize: file.size,
                status: 'init',
                statusText: i18n.t('LivingWitness.status_init') || '正在初始化上传...',
            })
            const initResult = await fileUploadInit({
                fileMd5,
                filename: file.name,
                filesize: file.size,
            })
            console.log('🚀 ~ fileUploadWithResume ~ fileUploadInit ~ initResult =====>', initResult)
            const { uploadId, offset: initialOffset, chunkSize } = initResult.data.result || initResult

            // 3. 分片上传
            let currentOffset = initialOffset || 0
            startTime = Date.now()
            lastTime = startTime
            lastUploadedBytes = currentOffset

            while (currentOffset < totalSize) {
                const end = Math.min(currentOffset + chunkSize, totalSize)
                const chunk = file.slice(currentOffset, end)

                const chunkStartTime = Date.now()
                const chunkResult = await fileUploadChunk({
                    uploadId,
                    chunk,
                    offset: currentOffset,
                    chunkSize,
                    fileSize: totalSize,
                })
                const chunkEndTime = Date.now()

                const { done, nextOffset } = chunkResult.data || chunkResult

                // 更新进度
                const uploadedBytes = nextOffset || end
                const percent = Math.round((uploadedBytes / totalSize) * 100)

                // 计算本分片速度
                const chunkTimeUsed = (chunkEndTime - lastTime) / 1000 // 秒
                const chunkBytes = uploadedBytes - lastUploadedBytes
                let speed = 0
                if (chunkTimeUsed > 0) {
                    speed = chunkBytes / chunkTimeUsed // B/s
                }
                // 换算速度
                let speedStr = ''
                if (speed >= 1024 * 1024) {
                    speedStr = (speed / 1024 / 1024).toFixed(2) + ' MB/s'
                } else {
                    speedStr = (speed / 1024).toFixed(2) + ' KB/s'
                }

                // 计算平均速度和剩余时间
                const now = chunkEndTime
                const timeUsed = (now - startTime) / 1000 // 秒
                const avgSpeed = timeUsed > 0 ? uploadedBytes / timeUsed : 0 // B/s
                let estimatedTime = 0
                if (avgSpeed > 0) {
                    const remainingBytes = totalSize - uploadedBytes
                    estimatedTime = remainingBytes / avgSpeed // 秒
                }

                onProgress({
                    percent,
                    uploadedBytes,
                    totalSize,
                    speed: speedStr,
                    estimatedTime: estimatedTime, // 单位：秒
                    status: 'uploading',
                    statusText: i18n.t('LivingWitness.status_uploading') || '正在上传...',
                })

                lastTime = chunkEndTime
                lastUploadedBytes = uploadedBytes

                if (done) {
                    break
                }

                currentOffset = nextOffset || end
            }

            // 4. 完成上传
            onProgress({
                percent: 100,
                uploadedBytes: totalSize,
                totalSize,
                speed: '0 KB/s',
                estimatedTime: 0,
                status: 'completing',
                statusText: i18n.t('LivingWitness.status_completing') || '正在处理文件...',
            })
            const completeResult = await fileUploadComplete(uploadId)

            onProgress({
                percent: 100,
                uploadedBytes: totalSize,
                totalSize,
                speed: '0 KB/s',
                estimatedTime: 0,
                status: 'done',
                statusText: i18n.t('LivingWitness.status_done') || '上传完成',
            })
            return completeResult.data.result || completeResult
        } catch (error) {
            console.error(`文件上传失败，第 ${currentRetry + 1} 次重试:`, error)
            currentRetry++

            if (currentRetry > retryTimes) {
                onError(error)
                throw new Error(`文件上传失败，已重试 ${retryTimes} 次: ${error.message}`)
            }

            // 等待一段时间后重试
            await new Promise(resolve => setTimeout(resolve, 1000 * currentRetry))
        }
    }
}
